// ABOUTME: Wrapper component that adapts SearchData props to DataTable interface.
// ABOUTME: Enables gradual migration from SearchData to @ds/data-table library.

import { QueryResult } from '@apollo/client';
import {
    type ColumnState,
    type CustomColumnDef,
    type DataGridAction,
    DataTable,
    type DataTableRemoteHandle,
    type DataTableRow,
    getLocaleDateTimeFormat,
    type GridSortModel,
    type SearchQueryVariables,
    toTitleCase,
} from '@ds/data-table';
import { useLanguageCtx } from '@modules/language';
import { useObjectSchemaSearchQuery } from '@modules/product';
import { useGetHierarchyLazyQuery, useGetHierarchyNodesBaseInfoLazyQuery } from '@modules/search';
import {
    type ContentSource,
    type Filters,
    type SearchFilter,
    SearchFilterOperator,
    type Sort,
} from '@src/__generated__/types';
import { UrlListFiltersChip } from '@src/_new/modules/list-filters';
import { isHierarchyMarketScoped, useMarketLabel } from '@src/_new/modules/market';
import { resolveWorkflowStateLabel, useWorkflowDefinitions } from '@src/_new/modules/workflow';
import { redirect } from '@src/_new/utils/redirect';
import type { SearchTableColumns } from '@src/assets/types/hierarchies/Hierarchies.types';
import { useSetContentMutation } from '@src/components/hierarchies/Hierarchy.generated';
import { useAuth } from '@src/hooks';
import { useGlStatuses } from '@src/modules/prototype/components/PrototypePanel/hooks/useGlStatuses';
import { SettingsContext } from '@src/modules/setting/SettingsContext';
import { ensureNameForLanguage } from '@src/utils/hierarchies/ensureNameForLanguage';
import type { Column, Row } from '@tanstack/react-table';
import { format } from 'date-fns';
import type { ComponentProps, Dispatch, MutableRefObject, SetStateAction } from 'react';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { ObjectSchemaSearchQuery } from '../../product/hooks/graphql/ObjectSchema.generated';
import { useGetShortlistsPrototypesObjectSchemaQuery } from '../../short-list/graphql/shortListPrototypes.generated';
import { asGridContentType } from './asGridContentType';
import {
    AssignedRefField,
    AssignedRefs,
    indexAssignedRefs,
    resolveAssignedRef,
} from './assignedRefs';
import {
    ASSIGNMENT_COLUMN_FIELDS,
    ASSIGNMENT_COLUMN_IDS,
    AssignmentColumnKey,
    getVisibleAssignmentColumnKeys,
} from './assignmentColumns';
import { asWorkflowStatusGridContentType } from './asWorkflowStatusGridContentType';
import GridCellCompatibilityRender from './cellRenderers/CGT/GridCellCompatibilityRender';
import { GridCellDisabledIndicator } from './cellRenderers/CGT/GridCellDisabledIndicator';
import { GridCellPositionTypeRenderer } from './cellRenderers/CGT/GridCellPositionTypeRenderer';
import { GridCellValidityStatusRenderer } from './cellRenderers/CGT/GridCellValidityStatusRenderer';
import { GridCellAssignedRefRender } from './cellRenderers/GridCellAssignedRefRender';
import GridCellDateRangeRender from './cellRenderers/GridCellDateRangeRender';
import GridCellFileRender from './cellRenderers/GridCellFileRender';
import GridCellHtmlRender from './cellRenderers/GridCellHtmlRender';
import GridCellImageRender from './cellRenderers/GridCellImageRender';
import {
    GridCellLinkedByRender,
    type LinkedByNodeClickHandler,
} from './cellRenderers/GridCellLinkedByRender';
import GridCellLinkRender, { LinkListNamesContext } from './cellRenderers/GridCellLinkRender';
import GridCellPrototypeRender from './cellRenderers/GridCellPrototypeRender';
import GridCellWorkflowStatusRender from './cellRenderers/GridCellWorkflowStatusRender';
import { useRemoteRowUpdates } from './hooks/useRemoteRowUpdates';
import { useTableColumnState } from './hooks/useTableColumnState';
import { useTableColumnsVisibility } from './hooks/useTableColumnsVisibility';
import {
    getLinkedByColumnHeaders,
    indexLinkedByColumns,
    LinkedByColumnGroup,
    linkedByColumnHierarchyId,
    linkedByColumnId,
    LinkedByHierarchies,
    linkedByRefLabel,
    mergeLinkedByHierarchies,
    resolveLinkedByGroup,
    toLinkedByLabelFilters,
} from './linkedByColumns';
import { indexLinkListNames, LinkListNamesIndex } from './linkListNames';
import { getShownContentColumnIds, seedTableFilters, splitPresetFilters } from './presetFilters';
import { RemoteConflictDialog } from './RemoteConflictDialog';
import type { RemoteUpdatesSchema } from './remoteUpdatesLabels';
import { RemoteUpdatesNotice } from './RemoteUpdatesNotice';

type BaseTableHandleRef = MutableRefObject<{
    getColumnsState: () => ColumnState[];
} | null>;

// Module-scoped to keep a stable reference; otherwise the data-table package
// rebuilds all columns on every parent render (its column memo lists the
// renderers map as a dep), which remounts cells and wipes their local state.
// Content types that never get the schema-derived `advancedFiltering` opt-in: image/file
// columns are not filterable at all.
const NON_EMPTY_FILTERABLE_CONTENT_TYPES = ['image', 'file', 'media'];

// Node attributes the data-table exposes as `system-<key>` columns (its "System" group) when
// they are listed in `extraColumns` or `visibleFields`. `linkedByField` is not one of them:
// the package builds it with a dedicated renderer and it stays in `extraColumns`.
const SYSTEM_COLUMN_KEYS = ['id', 'code', 'parent', 'nodePublished', 'createdAt', 'updatedAt'];
const SYSTEM_DATE_COLUMN_KEYS = ['createdAt', 'updatedAt'];
const isSystemColumnKey = (key: string) => SYSTEM_COLUMN_KEYS.includes(key);

const CUSTOM_CELL_RENDERERS = {
    prototype: GridCellPrototypeRender,
    date: GridCellDateRangeRender,
    dateRange: GridCellDateRangeRender,
    compatibilityCell: GridCellCompatibilityRender,
    // Editors bound to app services the design system cannot reach: the media API for file and
    // image, Quill plus the AI enhancement endpoint for html. Read-only they defer to the
    // library's own rendering through `DefaultRenderer`.
    html: GridCellHtmlRender,
    file: GridCellFileRender,
    image: GridCellFileRender,
    // Keyed by the type stored on the node's content, not by the schema's — which is why
    // translating the column to `image` is not enough for a `media` value. The package has no
    // branch of its own for the type, so without an entry here the cell falls through to its
    // default and prints the raw asset id. The preview renderer stands in: it shows the asset
    // and falls back to a named icon for a media that has none.
    media: GridCellImageRender,
    // A workflow status column is handed to the package as a `select` (see
    // asWorkflowStatusGridContentType) and its nodes store the status as either type; the renderer
    // draws the state chip on that column only and leaves every other select cell to the default.
    workflowStatus: GridCellWorkflowStatusRender,
    select: GridCellWorkflowStatusRender,
    // The package's link cell with the linked nodes labelled by their listName (read from
    // LinkListNamesContext); the values, the click, the filters and the export keep the codes.
    link: GridCellLinkRender,
};

// Opt-in variant that draws image columns as inline thumbnails instead of the data-table's
// icon-plus-tooltip default. Module-scoped for the same stable-reference reason as above.
const CUSTOM_CELL_RENDERERS_WITH_IMAGE_THUMBNAILS = {
    ...CUSTOM_CELL_RENDERERS,
    image: GridCellImageRender,
};

type DataTableWrapperProps = {
    baseTableId?: string;
    hierarchyIds: string[];
    editNodeId?: string;
    toolbar?: import('@ds/data-table').ToolbarConfig;
    visibleFields?: SearchTableColumns;
    extraColumns?: SearchTableColumns;
    // Which of the owner/dealership/client columns to render on an ownership-enabled schema.
    // Undefined renders all three; an empty array renders none.
    assignmentColumns?: AssignmentColumnKey[];
    withCheckbox?: boolean;
    objectSchemaCode: string;
    toggleSelection?: (
        selectedIds: string[],
        extraInfo?: (Record<string, string> | string)[],
        selectedNodes?: any[],
    ) => void;
    extraFilters?: SearchFilter[];
    selectedRecordIds?: string[];
    initialSelectedRecordIds?: string[];
    initialColsOrder?: string[];
    initialColsVisibility?: Record<string, boolean>;
    // When provided, the consumer owns the layout and nothing is persisted per user.
    initialColumnState?: ColumnState[];
    actions?: DataGridAction[];
    extraInfo?: Record<string, string>[];
    setExtraInfo?: Dispatch<SetStateAction<Record<string, string>[] | undefined>>;
    linkFieldId?: string;
    initialFilters?: SearchFilter[];
    dynamicFilters?: SearchFilter[];
    // Filters the table opens with (e.g. from a link): those on a shown column fill that column's
    // filter, where the user sees and changes them; the others apply as hidden extra filters,
    // named by a chip with `label` that `onClear` removes. They are read on mount: remount the
    // table (`key`) to apply new ones.
    presetFilters?: { filters: SearchFilter[]; label?: string; onClear: () => void };
    disabledFilters?: Filters[];
    disabled?: boolean;
    allowMultiSelect?: boolean;
    resetOnEmptyState?: boolean;
    isShortList?: boolean;
    onRowClick?: (row: Row<any>) => void;
    onVisibilityChange?: () => void;
    defaultLanguage: string;
    staticOptionsMap?: Record<string, { label: string; value: string; currency?: string }[]>;
    enableCellEditing?: boolean;
    handleRef?: BaseTableHandleRef;
    shortListParams?: {
        isShortList?: boolean;
        objectSchemaMetadata?: {
            shortListFamilyId?: string;
        };
    };
    useSearchArgs?: {
        queriesOverride?: {
            searchLazyQuery?: typeof useGetHierarchyLazyQuery;
            // TODO: understand how to use the generic type and not the custom one for CGT shortlist
            objectSchemasQuery?: // | typeof useObjectSchemaSearchQuery
            | typeof useGetShortlistsPrototypesObjectSchemaQuery
                | (() => QueryResult<ObjectSchemaSearchQuery, any>);
        };
    };
    nodeCode?: string;
    additionalInfoConfig?: any;
    colsConfig?: any;
    // Consumer-injected columns (e.g. computed/synthetic cells). Merged with the
    // internally-built CGT system columns and rendered via the data-table
    // `customColumns` extension point, which bypasses objectSchema form-field handling.
    customColumns?: CustomColumnDef[];
    pricelistOptions?: { id: string; name: string; code: string }[];
    showItemPositionType?: boolean;
    isGlLinkInCgtModel?: boolean;
    isItemLinkInGl?: boolean;
    originNode?: { hierarchyId: string; nodeId: string };
    onDataChange?: (data: { count: number; shownIds: string[]; shownParents: string[] }) => void;
    // Receives the filters and sorting of every search the table sends, e.g. to export the
    // rows it shows. Must keep a stable identity: a new callback re-issues the search.
    onSearch?: (query: Pick<SearchQueryVariables, 'filter' | 'sort'>) => void;
    // Client-side row grouping, forwarded as-is to the data-table package.
    grouping?: ComponentProps<typeof DataTable>['grouping'];
    // Reorders/filters the fetched edges before the data-table receives them, e.g. to
    // sort rows by a value the server cannot sort on. Runs on the current page only.
    transformSearchEdges?: (edges: any[]) => any[];
    // Reshapes the object schema the grid columns are built from, e.g. to inject a computed
    // content type into an existing group. Only the grid sees the result: the source schema
    // read by the editors through their own query is untouched.
    transformObjectSchema?: (schema: any) => any;
    /**
     * Bump this value to force the DataTable to re-call its search callback and
     * pick up fresh data. The data-table package owns its own row state and
     * does not react to Apollo `refetchQueries`; this is the supported way to
     * trigger a reload after an external mutation.
     */
    refreshKey?: number;
    /**
     * Render image columns as inline thumbnails (media ids resolved through the media endpoint)
     * instead of the data-table's default icon with tooltip preview. Off by default so tables
     * outside the custom pages keep their current look.
     */
    imageCellThumbnails?: boolean;
    /**
     * Tells the user, live, about the rows of the current page other users save or delete:
     * highlights them, patches the ones without an open editor and asks how to resolve the
     * others. Off by default.
     */
    liveUpdates?: boolean;
    /**
     * Which version of the nodes the rows show: omitted for the draft nodes, PUBLISHED for their
     * published version (filters and sort then apply to the published values).
     */
    contentSource?: ContentSource;
    /**
     * Sort sent to the search while the user has not sorted by a column, e.g. a line number order
     * for line items. Without it the rows come in the server's default order.
     */
    defaultSort?: Sort[];
    /**
     * Sort the table opens with, by column id (e.g. `system-code`): the search starts from it and
     * the column header shows its sort arrow, as if the user had sorted by it.
     */
    initialSorting?: GridSortModel;
    /**
     * Adds a "Linked by" section after the content: one column per hierarchy whose nodes link the
     * rows, headed by the hierarchy code, listing the linking nodes the user can read.
     * `hierarchyIds` narrows the hierarchies (absent or empty = every one found in the rows);
     * `labelFields` names, per linking hierarchy, the contents that label its nodes (default their
     * list name): several for one hierarchy are fallbacks, one naming no content reads the code, and
     * `linkedHierarchyId` reads the node it links in that hierarchy; without `onNodeClick` the
     * linking nodes are read-only.
     */
    linkedByColumns?: {
        hierarchyIds?: string[];
        labelFields?: {
            hierarchyId: string;
            contentTypeGroup?: string;
            contentType?: string;
            linkedHierarchyId?: string;
        }[];
        onNodeClick?: LinkedByNodeClickHandler;
    };
};

export function DataTableWrapper(props: DataTableWrapperProps) {
    const {
        hierarchyIds,
        objectSchemaCode,
        defaultLanguage,
        editNodeId,
        nodeCode,
        useSearchArgs,
        shortListParams,
        showItemPositionType,
        isGlLinkInCgtModel,
        isItemLinkInGl,
        extraInfo,
        setExtraInfo,
        extraFilters: propsExtraFilters,
        originNode,
        onDataChange,
        onSearch,
        refreshKey,
        customColumns: propCustomColumns,
        transformSearchEdges,
        transformObjectSchema,
        baseTableId,
        initialColsOrder,
        initialColsVisibility,
        initialColumnState,
        handleRef,
        onVisibilityChange,
        imageCellThumbnails = false,
        colsConfig: propsColsConfig,
        extraColumns: propExtraColumns,
        visibleFields: propVisibleFields,
        assignmentColumns,
        isShortList,
        liveUpdates,
        contentSource,
        defaultSort,
        initialSorting,
        linkedByColumns,
        initialFilters,
        presetFilters,
        ...restProps
    } = props;

    const intl = useIntl();

    // Get app contexts
    const { languages } = useLanguageCtx();
    const { hasUserRole, sessionData } = useAuth();
    // The market column follows the market module (present in `/api/user` only when it is on), on
    // tables of market-scoped hierarchies: nodes of shared hierarchies never carry a market.
    const marketEnabled = hierarchyIds.some(id =>
        isHierarchyMarketScoped(sessionData?.marketContext, id),
    );
    const { label: marketLabel } = useMarketLabel();
    const { searchDataPagination } = useContext(SettingsContext);

    // Memoize hierarchyIds for stable reference across renders
    const hierarchyIdsMemo = useMemo(() => hierarchyIds, [JSON.stringify(hierarchyIds)]);

    // Node ids shown on the current page. Fed to the batched GL-status query so the backend
    // computes statuses + compatible-price-list checks for on-screen rows only, and to the
    // live-updates subscription.
    const [shownIds, setShownIds] = useState<string[]>([]);

    const { glsStatuses } = useGlStatuses(isGlLinkInCgtModel ? editNodeId : '', shownIds);

    // Bumped by the live-updates notice to refetch the current page.
    const [refreshNonce, setRefreshNonce] = useState(0);
    const remoteHandleRef = useRef<DataTableRemoteHandle | null>(null);
    const {
        highlights,
        notice,
        conflicts,
        acceptConflict,
        keepMine,
        dismissNotice,
        reset: resetRemoteUpdates,
    } = useRemoteRowUpdates({
        enabled: !!liveUpdates,
        hierarchyIds: hierarchyIdsMemo,
        shownIds,
        remoteHandleRef,
    });

    // Get GraphQL hooks
    const [getHierarchyQuery] =
        useSearchArgs?.queriesOverride?.searchLazyQuery?.() || useGetHierarchyLazyQuery();

    const { data: objectSchemaData } =
        useSearchArgs?.queriesOverride?.objectSchemasQuery?.({
            variables: { metadata: { ...shortListParams?.objectSchemaMetadata } },
        }) || useObjectSchemaSearchQuery();

    const [setContentMutation] = useSetContentMutation();
    const [fetchHierarchyNodes] = useGetHierarchyNodesBaseInfoLazyQuery();

    // Build contexts
    const contexts = useMemo(
        () => ({
            language: {
                defaultLanguage,
                languages: languages || [],
            },
            auth: {
                hasUserRole,
            },
            settings: {
                pagination: searchDataPagination || undefined,
            },
            editNode: editNodeId
                ? {
                      nodeId: editNodeId,
                      hierarchyId: hierarchyIdsMemo[0],
                      nodeCode,
                      parent: undefined,
                  }
                : undefined,
            originNode,
        }),
        [
            defaultLanguage,
            languages,
            hasUserRole,
            searchDataPagination,
            editNodeId,
            hierarchyIdsMemo,
            nodeCode,
            originNode,
        ],
    );

    // The assigned owner/dealership/client/market references of the current page, by node id: the
    // data-table flattens each node into its own row shape and drops them, so the cells read here.
    const assignedRefsRef = useRef<Map<string, AssignedRefs>>(new Map());

    // The listNames of the nodes the link contents of the current page point to, by node id, read by
    // the link cells for the same reason. A query override that does not select them leaves it
    // empty, and the cells show the codes.
    const linkListNamesRef = useRef<LinkListNamesIndex>(new Map());

    // The "Linked by" groups of the current page, by node id and linking hierarchy, read by the
    // cells for the same reason, and the linking hierarchies seen so far, which the columns
    // follow. Consumers pass the option inline, so it is keyed by its content.
    const withLinkedByColumns = !!linkedByColumns;
    const linkedByHierarchyIdsKey = JSON.stringify(linkedByColumns?.hierarchyIds);
    const linkedByHierarchyIds = useMemo(
        () => linkedByColumns?.hierarchyIds,
        [linkedByHierarchyIdsKey],
    );
    const linkedByLabelFieldsKey = JSON.stringify(linkedByColumns?.labelFields);
    const linkedByLabelFields = useMemo(
        () => linkedByColumns?.labelFields,
        [linkedByLabelFieldsKey],
    );
    const linkedByRef = useRef<Map<string, Map<string, LinkedByColumnGroup>>>(new Map());
    const [linkedByHierarchies, setLinkedByHierarchies] = useState<LinkedByHierarchies>({});
    // A narrower hierarchy list drops the columns it no longer allows; the next page rebuilds them.
    useEffect(() => {
        setLinkedByHierarchies({});
    }, [linkedByHierarchyIds]);

    // Build search query
    const searchQuery = useCallback(
        async (variables: any) => {
            // The data-table always appends a `listName` tie-break to its sort: the user has
            // sorted by a column only when another field is there.
            const hasColumnSort = (variables.sort ?? []).some(
                (item: Sort) => item.field !== 'listName',
            );
            const sort =
                hasColumnSort || !defaultSort
                    ? variables.sort
                    : [...defaultSort, ...(variables.sort ?? [])];
            // Typed like the data-table's filters, which the query and `onSearch` take as they come.
            const filter: typeof variables.filter = toLinkedByLabelFilters(variables.filter);
            onSearch?.({ filter, sort });
            const result = await getHierarchyQuery({
                variables: {
                    ids: hierarchyIdsMemo,
                    pagination: variables.pagination,
                    sort,
                    filter,
                    ...(variables.originNode ? { originNode: variables.originNode } : {}),
                    ...(contentSource ? { contentSource } : {}),
                    ...(withLinkedByColumns
                        ? {
                              linkedByColumns: {
                                  hierarchyIds: linkedByHierarchyIds,
                                  labelFields: linkedByLabelFields,
                              },
                              withLinkedByColumns: true,
                          }
                        : {}),
                },
                fetchPolicy: 'no-cache',
            });

            const count = result.data?.searchHierarchies.nodes.count || 0;
            const rawEdges = (result.data?.searchHierarchies.nodes.edges || []) as any[];
            const edges = transformSearchEdges ? transformSearchEdges(rawEdges) : rawEdges;
            assignedRefsRef.current = indexAssignedRefs(edges);
            linkListNamesRef.current = indexLinkListNames(edges);
            if (withLinkedByColumns) {
                linkedByRef.current = indexLinkedByColumns(edges, linkedByHierarchyIds);
                setLinkedByHierarchies(seen =>
                    mergeLinkedByHierarchies(seen, edges, linkedByHierarchyIds),
                );
            }
            const pageIds = edges.map(e => e?.node?.id).filter(Boolean) as string[];
            setShownIds(pageIds);
            onDataChange?.({
                count,
                shownIds: pageIds,
                shownParents: Array.from(
                    new Set(edges.map(e => e?.node?.parent).filter(Boolean)),
                ) as string[],
            });

            return {
                edges,
                pageInfo: result.data?.searchHierarchies.nodes.pageInfo || {
                    endCursor: undefined,
                    hasNextPage: false,
                },
                count,
            };
        },
        // refreshKey and refreshNonce are intentionally part of the deps: bumping either
        // changes the callback identity, which is the only way to make the data-table
        // package re-issue its search query after an external mutation.
        [
            getHierarchyQuery,
            hierarchyIdsMemo,
            contentSource,
            withLinkedByColumns,
            linkedByHierarchyIds,
            linkedByLabelFields,
            defaultSort,
            onDataChange,
            onSearch,
            refreshKey,
            refreshNonce,
            transformSearchEdges,
        ],
    );

    // The workflows of the schema's workflowStatus columns: their states give each column its
    // options, labels, colors and icons.
    const workflowCodes = useMemo(() => {
        const schema = objectSchemaData?.objectSchemas?.find(
            (obj: any) => obj?.code === objectSchemaCode,
        );
        return (schema?.contentTypeGroups ?? []).flatMap((group: any) =>
            (group?.contentTypes ?? [])
                .filter((ct: any) => ct?.type === 'workflowStatus' && ct?.workflowCode)
                .map((ct: any) => ct.workflowCode as string),
        );
    }, [objectSchemaData, objectSchemaCode]);
    const { workflows, loading: workflowsLoading } = useWorkflowDefinitions(workflowCodes);

    // The object schema the grid builds its columns from; null until it is loaded.
    const gridSchema = useMemo(() => {
        const schema = objectSchemaData?.objectSchemas?.find(
            (obj: any) => obj?.code === objectSchemaCode,
        );
        if (!schema) {
            return null;
        }
        // A content type flagged with `showAsColumnWhenHidden` stays hidden in the
        // edit-node form but must be selectable as a table column. The data-table
        // library drops columns whose content type is `hidden`, so we present those
        // opted-in fields as non-hidden to the grid only (the source schema, read by
        // the editor through a different query, is left untouched).
        // The data-table library resolves group/column header names by an exact match on
        // `defaultLanguage` (the company default), while editNode resolves labels with the
        // browser locale first (getNameByLocale(name, [intl.locale, defaultLanguage])). We
        // rewrite the header name arrays so the library's exact lookup finds the value
        // resolved with the same browser-first priority, keeping the two views consistent.
        // Only `name` arrays are touched: `optionalValues` are also used to match stored
        // select values (duplicate-code composites, shortlist labels), so rewriting them
        // would corrupt data matching.
        const headerLanguages = [intl.locale, defaultLanguage];
        const schemaForGrid = {
            ...schema,
            contentTypeGroups: schema.contentTypeGroups?.map((group: any) => ({
                ...group,
                name: ensureNameForLanguage(group?.name, defaultLanguage, headerLanguages),
                contentTypes: group?.contentTypes?.map((ct: any) =>
                    asWorkflowStatusGridContentType(
                        asGridContentType({
                            ...(ct?.hidden && ct?.showAsColumnWhenHidden
                                ? { ...ct, hidden: false }
                                : ct),
                            name: ensureNameForLanguage(ct?.name, defaultLanguage, headerLanguages),
                        }),
                        workflows,
                        (workflow, stateCode) =>
                            resolveWorkflowStateLabel(intl, workflow, stateCode),
                        defaultLanguage,
                    ),
                ),
            })),
        };
        // Applied last, so the consumer works on the same shape the grid receives (header
        // names already resolved for the library's defaultLanguage lookup).
        return transformObjectSchema ? transformObjectSchema(schemaForGrid) : schemaForGrid;
    }, [
        objectSchemaData,
        objectSchemaCode,
        defaultLanguage,
        intl,
        transformObjectSchema,
        workflows,
    ]);

    // Build get object schema query
    const getObjectSchemaQuery = useCallback(async () => {
        if (!gridSchema) {
            throw new Error(`No Schema found for ${objectSchemaCode}`);
        }
        return gridSchema;
    }, [gridSchema, objectSchemaCode]);

    // Build set content mutation
    const setContentQuery = useCallback(
        async (variables: any) => {
            const result = await setContentMutation({
                variables: {
                    aggregateId: variables.aggregateId,
                    node: variables.node,
                    contents: variables.contents,
                    skipTranslation: variables.skipTranslation,
                },
            });
            return {
                updateNodeContent: result.data?.updateNodeContent || {
                    success: false,
                    code: 'ERROR',
                    message: 'Mutation failed',
                },
            };
        },
        [setContentMutation],
    );

    // Build hierarchy nodes query adapter for LinkFilter. The options are found and labelled like the
    // link cells show the linked nodes: by listName, else by code (the package labels them by `code`).
    const getHierarchyNodesQuery = useCallback(
        async (variables: any) => {
            let filter: any = [];
            if (variables.filters?.code) {
                filter = {
                    field: 'codeOrListName',
                    operator: SearchFilterOperator.Contains,
                    value: [variables.filters.code],
                };
            } else if (variables.filters?.ids) {
                filter = {
                    field: 'id',
                    operator: SearchFilterOperator.IsAnyOf,
                    value: variables.filters.ids,
                };
            }
            const result = await fetchHierarchyNodes({
                variables: {
                    id: variables.id,
                    filter,
                    sort: [],
                    ...(variables.pagination?.first ? { first: variables.pagination.first } : {}),
                },
                fetchPolicy: 'network-only',
            });
            return {
                hierarchies: [
                    {
                        nodes:
                            result.data?.searchHierarchies.nodes.edges.map(e => ({
                                id: e.node.id,
                                code: e.node.listName || e.node.code,
                            })) ?? [],
                        count: result.data?.searchHierarchies.nodes.count ?? 0,
                    },
                ],
            };
        },
        [fetchHierarchyNodes],
    );

    // Build queries object
    const queries = useMemo(
        () => ({
            search: searchQuery,
            getObjectSchema: getObjectSchemaQuery,
            setContent: setContentQuery,
            getHierarchyNodes: getHierarchyNodesQuery,
        }),
        [searchQuery, getObjectSchemaQuery, setContentQuery, getHierarchyNodesQuery],
    );

    // Build filters from hierarchyIds and extraFilters
    const extraFilters = useMemo(() => {
        const filters: any[] = [];

        // Always add hierarchy filter
        filters.push({
            field: 'hierarchy',
            operator: SearchFilterOperator.IsAnyOf,
            value: hierarchyIdsMemo,
        });

        // Merge with additional filters if provided
        if (propsExtraFilters) {
            filters.push(...propsExtraFilters);
        }

        return filters;
    }, [hierarchyIdsMemo, propsExtraFilters]);

    const onLinkClick = (hierarchyId: string, nodeId: string) => {
        redirect({ hierarchy: hierarchyId, node: nodeId });
    };

    // CGT cell render callbacks (stable references to avoid nested component lint errors)
    const renderPositionType = useCallback(
        ({ row }: { row: Row<any> }) => (
            <GridCellPositionTypeRenderer
                row={row}
                extraInfo={extraInfo}
                setExtraInfo={setExtraInfo!}
            />
        ),
        [extraInfo, setExtraInfo],
    );

    const renderValidityStatus = useCallback(
        ({ row }: { row: Row<any> }) => (
            <GridCellValidityStatusRenderer
                modelId={editNodeId!}
                row={row}
                glsStatuses={glsStatuses}
            />
        ),
        [editNodeId, glsStatuses],
    );

    const renderDisabledIndicator = useCallback(
        ({ row }: { row: Row<any> }) => <GridCellDisabledIndicator row={row} />,
        [],
    );

    // Show owner/dealership/client columns only when the object schema enables node ownership.
    // Cast to any because objectSchemaData may come from an override query whose schema shape
    // does not declare enableOwner (only the default ObjectSchemaSearch query does).
    const ownerEnabled = useMemo(
        () =>
            !!(
                objectSchemaData?.objectSchemas?.find(
                    (obj: any) => obj?.code === objectSchemaCode,
                ) as any
            )?.enableOwner,
        [objectSchemaData, objectSchemaCode],
    );

    const getAssignedRef = useCallback(
        (row: DataTableRow, field: AssignedRefField) =>
            resolveAssignedRef(row as Record<string, unknown>, field, assignedRefsRef.current),
        [],
    );
    const renderAssignedOwner = useCallback(
        ({ row }: { row: Row<any> }) => (
            <GridCellAssignedRefRender
                row={row}
                field="assignedOwner"
                index={assignedRefsRef.current}
            />
        ),
        [],
    );
    const renderAssignedDealership = useCallback(
        ({ row }: { row: Row<any> }) => (
            <GridCellAssignedRefRender
                row={row}
                field="assignedDealership"
                index={assignedRefsRef.current}
            />
        ),
        [],
    );
    const renderAssignedClient = useCallback(
        ({ row }: { row: Row<any> }) => (
            <GridCellAssignedRefRender
                row={row}
                field="assignedClient"
                index={assignedRefsRef.current}
            />
        ),
        [],
    );
    const renderAssignedMarket = useCallback(
        ({ row }: { row: Row<any> }) => (
            <GridCellAssignedRefRender
                row={row}
                field="assignedMarket"
                index={assignedRefsRef.current}
            />
        ),
        [],
    );

    // The click handler is read through a ref: consumers pass it inline, and a new handler on every
    // render must not rebuild the columns.
    const linkedByClickRef = useRef<LinkedByNodeClickHandler | undefined>();
    linkedByClickRef.current = linkedByColumns?.onNodeClick;
    const linkedByClickable = !!linkedByColumns?.onNodeClick;
    const handleLinkedByClick = useCallback<LinkedByNodeClickHandler>(
        ref => linkedByClickRef.current?.(ref),
        [],
    );
    const renderLinkedBy = useCallback(
        ({ row, column }: { row: Row<any>; column: Column<any> }) => (
            <GridCellLinkedByRender
                group={resolveLinkedByGroup(
                    row.original as Record<string, unknown>,
                    linkedByColumnHierarchyId(column.id),
                    linkedByRef.current,
                )}
                onNodeClick={linkedByClickable ? handleLinkedByClick : undefined}
            />
        ),
        [linkedByClickable, handleLinkedByClick],
    );

    // Filter options for select columns with allowDuplicateOptionsCode = true must encode
    // both the option code and its label so the search filter can disambiguate options that
    // share a code. The Neo4j read-model property holds both the bare code and the composite
    // "code - label" entry, and the server-side comparator does exact matching on each
    // element of the array, so sending the composite from the filter UI narrows the result
    // set to the specific option the user picked.
    const staticOptionsMap = useMemo<
        Record<string, { label: string; value: string }[]> | undefined
    >(() => {
        const schema = objectSchemaData?.objectSchemas?.find(
            (obj: any) => obj?.code === objectSchemaCode,
        );
        if (!schema) return undefined;
        const map: Record<string, { label: string; value: string }[]> = {};
        schema.contentTypeGroups?.forEach((group: any) => {
            group?.contentTypes?.forEach((ct: any) => {
                const hasDuplicateCodeOptions =
                    ct?.type === 'select' &&
                    ct?.allowDuplicateOptionsCode === true &&
                    Array.isArray(ct?.optionalValues) &&
                    ct.optionalValues.length > 0;
                if (!hasDuplicateCodeOptions) return;
                map[ct.code] = ct.optionalValues.map((opt: any) => {
                    const label =
                        opt.value?.find((v: any) => v.languageIsoCode === defaultLanguage)?.value ??
                        opt.value?.[0]?.value ??
                        opt.code;
                    return { label, value: `${opt.code} - ${label}` };
                });
            });
        });
        return Object.keys(map).length > 0 ? map : undefined;
    }, [objectSchemaData, objectSchemaCode, defaultLanguage]);

    // The data-table package only offers the "empty values" filter (the `(Empty)` option on
    // link/select autocompletes and the toggle on text/date inputs) on columns that opt in
    // through `colsConfig.columns[<ctg>-<ct>].filter.advancedFiltering`. Every filterable
    // column of the schema opts in here so the filter is available on all tables. On number
    // columns the same flag also swaps the plain text filter for the numeric operators popover
    // (`>`, `<`, between, is empty / is not empty).
    const advancedFilteringColumns = useMemo<
        Record<string, { filter: { advancedFiltering: boolean } }> | undefined
    >(() => {
        const schema = objectSchemaData?.objectSchemas?.find(
            (obj: any) => obj?.code === objectSchemaCode,
        );
        if (!schema) return undefined;
        const map: Record<string, { filter: { advancedFiltering: boolean } }> = {};
        schema.contentTypeGroups?.forEach((group: any) => {
            group?.contentTypes?.forEach((ct: any) => {
                if (NON_EMPTY_FILTERABLE_CONTENT_TYPES.includes(ct?.type)) return;
                map[`${group.code}-${ct.code}`] = { filter: { advancedFiltering: true } };
            });
        });
        return Object.keys(map).length > 0 ? map : undefined;
    }, [objectSchemaData, objectSchemaCode]);

    // Caller-provided per-column config wins over the schema-derived opt-in.
    const colsConfig = useMemo(() => {
        if (!advancedFilteringColumns) return propsColsConfig;
        return {
            ...propsColsConfig,
            columns: { ...advancedFilteringColumns, ...propsColsConfig?.columns },
        };
    }, [propsColsConfig, advancedFilteringColumns]);

    // The data-table builds its system columns (code, parent, createdAt, ...) on its own from
    // `extraColumns`/`visibleFields`, without reading the per-column filter config, so they would
    // never offer the "empty values" filter. They are therefore taken out of those props and
    // re-declared here as custom columns, mirroring the package's own definitions (header, date
    // formatting, sizing, pinning) plus the `advancedFiltering` opt-in.
    // Short lists are left to the package: it always builds `system-code` itself in that mode,
    // and a second definition would duplicate the column.
    const buildsSystemColumns = !isShortList && !shortListParams?.isShortList;
    const systemColumnKeys = useMemo(() => {
        if (!buildsSystemColumns) return [];
        const keys = [...(propVisibleFields ?? []), ...(propExtraColumns ?? [])].filter(
            isSystemColumnKey,
        );
        return Array.from(new Set(keys));
    }, [buildsSystemColumns, propVisibleFields, propExtraColumns]);
    const visibleFields = useMemo(
        () =>
            buildsSystemColumns
                ? propVisibleFields?.filter(key => !isSystemColumnKey(key))
                : propVisibleFields,
        [buildsSystemColumns, propVisibleFields],
    );
    const extraColumns = useMemo(
        () =>
            buildsSystemColumns
                ? propExtraColumns?.filter(key => !isSystemColumnKey(key))
                : propExtraColumns,
        [buildsSystemColumns, propExtraColumns],
    );
    // Depends on the two primitives it reads, not on `propsColsConfig`: consumers pass that
    // object inline, and a new column model on every render would reset the grid's column state.
    const systemColumnSize = propsColsConfig?.size ?? 220;
    const systemColumnsAllowUserPinning = propsColsConfig?.pinning?.allowUserPinning;
    const systemCustomColumns = useMemo<CustomColumnDef[]>(() => {
        const size = systemColumnSize;
        const systemGroupHeader = intl.formatMessage({
            id: 'search-data-grid.column.system',
            defaultMessage: 'System',
        });
        return systemColumnKeys.map(key => {
            const isDate = SYSTEM_DATE_COLUMN_KEYS.includes(key);
            return {
                columnDef: {
                    accessorKey: `system-${key}`,
                    header: intl.formatMessage(
                        { id: 'search-data-grid.column.{key}', defaultMessage: '{key}' },
                        { key: toTitleCase(key) },
                    ),
                    size,
                    minSize: size,
                    ...(isDate
                        ? {
                              cell: ({ getValue }) =>
                                  getValue()
                                      ? format(getValue() as number, getLocaleDateTimeFormat())
                                      : '',
                              filterFn: undefined,
                              // The package's date filter throws "Can not find utils in context":
                              // its bundle inlines DatePicker but imports LocalizationProvider
                              // from the app copy of @mui/x-date-pickers, so the two never share
                              // a context. No filter until the package build externalizes the
                              // picker subpaths.
                              enableColumnFilter: false,
                          }
                        : {}),
                    meta: {
                        allowUserPinning: systemColumnsAllowUserPinning,
                        filter: {
                            type: isDate ? 'date' : 'text',
                            contentType: '',
                            advancedFiltering: true,
                        },
                    },
                },
                position: 'before-content',
                groupId: 'system',
                groupHeader: systemGroupHeader,
            };
        });
    }, [systemColumnKeys, systemColumnSize, systemColumnsAllowUserPinning, intl]);

    // Build CGT-specific custom columns
    const customColumns = useMemo(() => {
        const cols: CustomColumnDef[] = [];

        const systemGroupHeader = intl.formatMessage({
            id: 'search-data-grid.column.system',
            defaultMessage: 'System',
        });

        if (showItemPositionType && setExtraInfo) {
            cols.push({
                columnDef: {
                    id: 'system-position-type',
                    header: intl.formatMessage({
                        id: 'search-data-grid.column.system-position-type',
                        defaultMessage: 'Tipo Posizione',
                    }),
                    size: 170,
                    enableSorting: false,
                    enableColumnFilter: false,
                    cell: renderPositionType,
                },
                position: 'before-content',
                groupId: 'system',
                groupHeader: systemGroupHeader,
            });
        }

        if (editNodeId && isGlLinkInCgtModel) {
            cols.push({
                columnDef: {
                    id: 'system-validity-status',
                    header: intl.formatMessage({
                        id: 'search-data-grid.column.validity-status',
                        defaultMessage: 'Validity Status',
                    }),
                    size: 100,
                    enableSorting: false,
                    enableColumnFilter: false,
                    cell: renderValidityStatus,
                },
                position: 'before-content',
                groupId: 'system',
                groupHeader: systemGroupHeader,
            });
        }

        if (isItemLinkInGl) {
            cols.push({
                columnDef: {
                    id: 'system-disabled',
                    header: intl.formatMessage({
                        id: 'search-data-grid.column.disabled',
                        defaultMessage: 'Disabled',
                    }),
                    size: 25,
                    enableSorting: false,
                    enableColumnFilter: false,
                    cell: renderDisabledIndicator,
                },
                position: 'after-content',
                groupId: 'system',
                groupHeader: systemGroupHeader,
            });
        }

        // The assignment columns sit before the content, next to the code and the other System
        // columns: the data-table groups adjacent columns only, so placed after the content they
        // would open a second "System" header.
        const visibleAssignmentKeys = getVisibleAssignmentColumnKeys({
            ownerEnabled,
            marketEnabled,
            assignmentColumns,
        });
        if (visibleAssignmentKeys.length > 0) {
            const assignmentColumnDefs: Record<AssignmentColumnKey, CustomColumnDef> = {
                owner: {
                    columnDef: {
                        id: ASSIGNMENT_COLUMN_IDS.owner,
                        // The accessor makes the column filterable: the filter is sent on the
                        // column id and matched server-side on the assigned node's id, code and listName.
                        accessorFn: (row: DataTableRow) =>
                            getAssignedRef(row, ASSIGNMENT_COLUMN_FIELDS.owner)?.code ?? '',
                        header: intl.formatMessage({
                            id: 'search-data-grid.column.assigned-owner',
                            defaultMessage: 'Owner',
                        }),
                        size: 180,
                        enableSorting: false,
                        cell: renderAssignedOwner,
                        meta: {
                            filter: { type: 'text', contentType: '', advancedFiltering: true },
                        },
                    },
                    position: 'before-content',
                    groupId: 'system',
                    groupHeader: systemGroupHeader,
                },
                dealership: {
                    columnDef: {
                        id: ASSIGNMENT_COLUMN_IDS.dealership,
                        accessorFn: (row: DataTableRow) =>
                            getAssignedRef(row, ASSIGNMENT_COLUMN_FIELDS.dealership)?.code ?? '',
                        header: intl.formatMessage({
                            id: 'search-data-grid.column.assigned-dealership',
                            defaultMessage: 'Dealership',
                        }),
                        size: 180,
                        enableSorting: false,
                        cell: renderAssignedDealership,
                        meta: {
                            filter: { type: 'text', contentType: '', advancedFiltering: true },
                        },
                    },
                    position: 'before-content',
                    groupId: 'system',
                    groupHeader: systemGroupHeader,
                },
                client: {
                    columnDef: {
                        id: ASSIGNMENT_COLUMN_IDS.client,
                        accessorFn: (row: DataTableRow) =>
                            getAssignedRef(row, ASSIGNMENT_COLUMN_FIELDS.client)?.code ?? '',
                        header: intl.formatMessage({
                            id: 'search-data-grid.column.assigned-client',
                            defaultMessage: 'Client',
                        }),
                        size: 180,
                        enableSorting: false,
                        cell: renderAssignedClient,
                        meta: {
                            filter: { type: 'text', contentType: '', advancedFiltering: true },
                        },
                    },
                    position: 'before-content',
                    groupId: 'system',
                    groupHeader: systemGroupHeader,
                },
                market: {
                    columnDef: {
                        id: ASSIGNMENT_COLUMN_IDS.market,
                        header: marketLabel,
                        size: 180,
                        enableSorting: false,
                        enableColumnFilter: false,
                        cell: renderAssignedMarket,
                    },
                    position: 'before-content',
                    groupId: 'system',
                    groupHeader: systemGroupHeader,
                },
            };
            visibleAssignmentKeys.forEach(key => cols.push(assignmentColumnDefs[key]));
        }

        if (withLinkedByColumns) {
            const linkedByGroupHeader = intl.formatMessage({
                id: 'search-data-grid.column.linkedBy',
                defaultMessage: 'Linked by',
            });
            getLinkedByColumnHeaders(linkedByHierarchies).forEach(({ hierarchyId, header }) =>
                cols.push({
                    columnDef: {
                        id: linkedByColumnId(hierarchyId),
                        // The accessor makes the column filterable: the filter is sent on the
                        // column id and matched server-side on the label the cell shows.
                        accessorFn: (row: DataTableRow) =>
                            resolveLinkedByGroup(row, hierarchyId, linkedByRef.current)
                                ?.items.map(linkedByRefLabel)
                                .join(', ') ?? '',
                        header,
                        size: 220,
                        enableSorting: false,
                        cell: renderLinkedBy,
                        meta: {
                            filter: { type: 'text', contentType: '', advancedFiltering: true },
                        },
                    },
                    position: 'after-content',
                    groupId: 'linked-by',
                    groupHeader: linkedByGroupHeader,
                }),
            );
        }

        return cols;
    }, [
        showItemPositionType,
        isGlLinkInCgtModel,
        isItemLinkInGl,
        editNodeId,
        setExtraInfo,
        intl,
        renderPositionType,
        renderValidityStatus,
        renderDisabledIndicator,
        ownerEnabled,
        marketEnabled,
        marketLabel,
        assignmentColumns,
        getAssignedRef,
        renderAssignedOwner,
        renderAssignedDealership,
        renderAssignedClient,
        renderAssignedMarket,
        withLinkedByColumns,
        linkedByHierarchies,
        renderLinkedBy,
    ]);

    // Merge internally-built system and CGT columns with consumer-injected columns.
    // A before-content column without a groupId is appended by the data-table to the System
    // group it builds itself. With the system columns declared here, that group would only hold
    // such columns and show up as a second "System" header, so they are moved into this group.
    const mergedCustomColumns = useMemo(() => {
        const systemGroupHeader = intl.formatMessage({
            id: 'search-data-grid.column.system',
            defaultMessage: 'System',
        });
        const joinSystemGroup = (column: CustomColumnDef): CustomColumnDef =>
            systemCustomColumns.length > 0 &&
            (column.position ?? 'before-content') === 'before-content' &&
            !column.groupId
                ? { ...column, groupId: 'system', groupHeader: systemGroupHeader }
                : column;
        const merged = [
            ...systemCustomColumns,
            ...customColumns.map(joinSystemGroup),
            ...(propCustomColumns ?? []).map(joinSystemGroup),
        ];
        return merged.length > 0 ? merged : undefined;
    }, [systemCustomColumns, customColumns, propCustomColumns, intl]);

    // Every table that identifies itself with a `baseTableId` stores the user's column
    // order and visibility together; `initialColsOrder`/`initialColsVisibility` then seed
    // the table only until the user first customizes it. A consumer-provided
    // `initialColumnState` (e.g. the shortlist, stored on the family node) opts out.
    const persistColumnLayout = !!baseTableId && !initialColumnState;

    const {
        columnState: userColumnState,
        loaded: userColumnStateLoaded,
        save: saveColumnState,
    } = useTableColumnState(baseTableId, persistColumnLayout);

    // Legacy, read-only: the visibility-only store is still read to seed a table whose
    // user has no `tableColumnStates` entry yet, so nobody loses their hidden columns.
    // It is never written any more. Tables that already receive a configured visibility
    // (`initialColsVisibility`, e.g. the prototype table) or a full layout
    // (`initialColumnState`) keep those as their source of truth.
    const readLegacyColsVisibility = !!baseTableId && !initialColsVisibility && !initialColumnState;

    const { visibility: userColsVisibility, loaded: userColsVisibilityLoaded } =
        useTableColumnsVisibility(baseTableId, readLegacyColsVisibility);

    // The column state is read through the table handle; consumers that do not need the
    // handle themselves still get one so their layout can be persisted.
    const internalHandleRef = useRef<{ getColumnsState: () => ColumnState[] } | null>(null);
    const columnsHandleRef = handleRef ?? internalHandleRef;

    // Fired by the column manager both on a show/hide toggle and after a drag reorder, so
    // the snapshot it takes carries the user's full layout.
    const handleVisibilityChange = useCallback(() => {
        onVisibilityChange?.();

        if (!persistColumnLayout) return;

        const columnsState = columnsHandleRef.current?.getColumnsState();
        // Empty while the column manager has not built its column list yet: saving then
        // would wipe the stored layout.
        if (!columnsState?.length) return;

        saveColumnState(columnsState);
    }, [onVisibilityChange, persistColumnLayout, columnsHandleRef, saveColumnState]);

    // When the user has a saved layout, it drives both order and visibility, so the
    // settings-derived seed props are dropped to avoid fighting the restored state.
    const hasPersistedColumnState = persistColumnLayout && !!userColumnState;
    const effectiveInitialColumnState = initialColumnState ?? userColumnState;
    const effectiveInitialColsOrder = hasPersistedColumnState ? undefined : initialColsOrder;
    const effectiveInitialColsVisibility = hasPersistedColumnState
        ? undefined
        : initialColsVisibility ?? userColsVisibility;

    // The preset filters on a shown content type column seed its filter, the others stay hidden.
    const { columnFilters: presetColumnFilters, hiddenFilters: presetHiddenFilters } = useMemo(
        () =>
            splitPresetFilters(
                presetFilters?.filters ?? [],
                gridSchema
                    ? getShownContentColumnIds(
                          gridSchema,
                          effectiveInitialColumnState,
                          effectiveInitialColsVisibility,
                      )
                    : new Set(),
            ),
        [presetFilters, gridSchema, effectiveInitialColumnState, effectiveInitialColsVisibility],
    );
    const tableExtraFilters = useMemo(
        () =>
            presetHiddenFilters.length ? [...extraFilters, ...presetHiddenFilters] : extraFilters,
        [extraFilters, presetHiddenFilters],
    );
    // The data-table restores its filter model from the session when it has an `id`: seeding it
    // with the preset column filters makes its first search already filtered and shows them in
    // their column filters. Seeded while rendering, as the table reads it when it mounts.
    const presetTableId = presetColumnFilters.length
        ? `preset-${baseTableId ?? objectSchemaCode}`
        : undefined;
    useMemo(() => {
        if (presetTableId) seedTableFilters(presetTableId, presetColumnFilters);
    }, [presetTableId, presetColumnFilters]);

    const tableSchema = useMemo(
        () =>
            objectSchemaData?.objectSchemas?.find((obj: any) => obj?.code === objectSchemaCode) as
                | RemoteUpdatesSchema
                | undefined,
        [objectSchemaData, objectSchemaCode],
    );

    const refreshRemoteUpdates = useCallback(() => {
        resetRemoteUpdates();
        setRefreshNonce(nonce => nonce + 1);
    }, [resetRemoteUpdates]);

    const getRemoteNode = useCallback(
        (nodeId: string) => remoteHandleRef.current?.getNode(nodeId),
        [],
    );

    // The table reads its column visibility once, when mounting, so it must not render
    // before the stored value is known.
    // Nor before the workflows of its status columns are known, which shape those columns.
    // Nor, with preset filters, before its schema tells which of them a shown column takes: the
    // column filters are read once, when mounting.
    if (
        !userColsVisibilityLoaded ||
        !userColumnStateLoaded ||
        workflowsLoading ||
        (!!presetFilters?.filters.length && !gridSchema)
    ) {
        return null;
    }

    // Pass through to DataTable with transformed props
    const table = (
        <LinkListNamesContext.Provider value={linkListNamesRef}>
            <DataTable
                contexts={contexts}
                queries={queries}
                extraFilters={tableExtraFilters}
                initialFilters={initialFilters}
                // Known up front, so a first search matching nothing still renders the columns.
                objectSchemaCode={objectSchemaCode}
                onLinkClick={onLinkClick}
                customCellRenderers={
                    imageCellThumbnails
                        ? CUSTOM_CELL_RENDERERS_WITH_IMAGE_THUMBNAILS
                        : CUSTOM_CELL_RENDERERS
                }
                customColumns={mergedCustomColumns}
                staticOptionsMap={staticOptionsMap}
                extraInfo={extraInfo}
                setExtraInfo={setExtraInfo}
                excludeDndProvider
                editNodeId={editNodeId}
                {...(restProps as any)}
                colsConfig={colsConfig}
                extraColumns={extraColumns}
                visibleFields={visibleFields}
                isShortList={isShortList}
                baseTableId={baseTableId}
                initialColsOrder={effectiveInitialColsOrder}
                initialColsVisibility={effectiveInitialColsVisibility}
                initialColumnState={effectiveInitialColumnState}
                initialSorting={initialSorting}
                handleRef={columnsHandleRef}
                onVisibilityChange={handleVisibilityChange}
                remoteHandleRef={remoteHandleRef}
                cellHighlights={liveUpdates ? highlights : undefined}
                {...(presetTableId ? { id: presetTableId } : {})}
            />
        </LinkListNamesContext.Provider>
    );

    // The preset filters no shown column takes are named by a chip, whose delete clears them.
    const presetChip =
        presetFilters && presetHiddenFilters.length ? (
            <UrlListFiltersChip
                listFilters={{ label: presetFilters.label, filters: presetHiddenFilters }}
                onClear={presetFilters.onClear}
            />
        ) : null;

    if (!liveUpdates) {
        return presetChip ? (
            <>
                {presetChip}
                {table}
            </>
        ) : (
            table
        );
    }

    return (
        <>
            <RemoteUpdatesNotice
                notice={notice}
                schema={tableSchema}
                defaultLanguage={defaultLanguage}
                onRefresh={refreshRemoteUpdates}
                onDismiss={dismissNotice}
            />
            {presetChip}
            {table}
            <RemoteConflictDialog
                conflict={Object.values(conflicts)[0]}
                schema={tableSchema}
                defaultLanguage={defaultLanguage}
                getNode={getRemoteNode}
                onKeepMine={keepMine}
                onAccept={acceptConflict}
            />
        </>
    );
}
